import React, { useEffect, useState } from 'react';
import './styles.css';
import { FaChevronDown, FaChevronUp, FaAngleDoubleLeft, FaAngleDoubleRight, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import MaterialSelect from '../../../../components/MaterialSelect';
import ConfirmationDialog from '../../../../components/Dialog/ConfirmationDialog';
import MaterialInput from '../../../../components/MaterialInput';
import { Stack, Tooltip } from '@mui/material';
import MaterialTypography from '../../../../components/MaterialTypography';
import axios from 'axios';
import { BASE_URL } from '../../../Utils/Baseurl';
import { useSnackbar } from '../../../../components/SnackbarProvider';
import { useLoading } from '../../../../context/loadingcontext';

const EmployeeTaskTable = ({ columns, data, onTaskUpdate, totalRows, currentPage, onPageChange, onSort, sortConfig, rowsPerPage, onRowsPerPageChange }) => {

    const { showSnackbar } = useSnackbar();
    const { setCirleLoading } = useLoading();
    const [tableData, setTableData] = useState(data);
    const [expandedRows, setExpandedRows] = useState([]);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [actualHours, setActualHours] = useState('');
    const [reasons, setReasons] = useState('');

    // Update local state when prop changes
    useEffect(() => {
        setTableData(data);
    }, [data]);

    const toggleRow = (id) => {
        setExpandedRows(prev =>
            prev.includes(id)
                ? prev.filter(rowId => rowId !== id)
                : [...prev, id]
        );
    };

    const truncateText = (text, maxLength = 15) => {
        if (!text) return '-';
        const textStr = String(text);
        if (textStr.length <= maxLength) return textStr;
        return textStr.substring(0, maxLength) + '...';
    };

    // Filter columns based on showDisplay property and whether they have data for specific columns
    const displayColumns = columns.filter(col => {
        if (col.showDisplay !== true) return false;

        // Only show columns if at least one row has a non-empty value for specific keys
        if (['reason', 'cancel_reason', 'hold_date', 'reason_hours'].includes(col.key)) {
            return data.some(row => row[col.key] && String(row[col.key]).trim() !== '');
        }

        return true;
    });
    const collapsedColumns = columns.filter(col => {
        if (col.showDisplay === true || col.key === 'action') return false;

        // Apply same dynamic logic to collapsed columns
        if (['reason', 'cancel_reason', 'hold_date', 'reason_hours'].includes(col.key)) {
            return data.some(row => row[col.key] && String(row[col.key]).trim() !== '');
        }

        return true;
    });

    // Pagination calculations (Server-side)
    const totalPages = Math.ceil(totalRows / rowsPerPage);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + data.length; // Adjusted for server-side
    const currentData = data; // Data is already sliced by server


    // ... (keep pagination handlers)

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            onPageChange(page);
            setExpandedRows([]); // Collapse all rows when changing page
        }
    };


    const handleRowsPerPageChange = (e) => {
        const newValue = Number(e.target.value);
        if (onRowsPerPageChange) {
            onRowsPerPageChange(newValue);
        }
        setExpandedRows([]);
    };


    // ... (keep getPageNumbers)
    const getPageNumbers = () => {
        const pages = [];
        const maxPagesToShow = 5;

        if (totalPages <= maxPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (currentPage <= 3) {
                for (let i = 1; i <= 4; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
            } else {
                pages.push(1);
                pages.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            }
        }
        return pages;
    };


    const handleStatusChange = async (option, row) => {

        if (['Completed', 'Hold', 'Abort'].includes(option.value)) {
            setSelectedRow(row);
            setSelectedStatus(option);
            setDialogOpen(true);
        } else if (option.value === 'In Progress') {

            try {
                const token = sessionStorage.getItem("token");
                const payload = {
                    status: 'In Progress',
                    actual_hours: '0',
                    comment: ''
                };
                setCirleLoading(true);

                const response = await axios.post(
                    `${BASE_URL}api/updatetaskstatus/${row.id}/`,
                    payload,
                    {
                        headers: {
                            "Authorization": `Bearer ${token}`,
                            "Content-Type": "application/json"
                        }
                    }
                );

                if (response.data?.status || response.status === 200) {
                    const newData = tableData.map(item =>
                        item.id === row.id ? { ...item, status: 'In Progress' } : item
                    );
                    setTableData(newData);

                    showSnackbar(response.data?.message || 'Task status updated to In Progress', 'success');

                    if (onTaskUpdate) {
                        onTaskUpdate();
                    }
                } else {
                    showSnackbar(response.data?.message || 'Failed to update task status', 'error');
                }
            } catch (error) {
                console.error('Error updating task status to In Progress:', error);
                const errorMessage = error?.response?.data?.message || error?.message || 'An error occurred while updating task status';
                showSnackbar(errorMessage, 'error');
            } finally {
                setCirleLoading(false);
            }
        } else {
            const newData = tableData.map(item =>
                item.id === row.id ? { ...item, status: option.value } : item
            );
            setTableData(newData);
        }
    };

    const handleDialogConfirm = async () => {
        if (!actualHours || actualHours.trim() === '') {
            showSnackbar('Actual hours is mandatory for all statuses', 'error');
            return;
        }

        if (['Abort', 'Hold'].includes(selectedStatus?.value)) {
            if (!reasons || reasons.trim() === '') {
                showSnackbar(`Comment is mandatory for ${selectedStatus?.value} status`, 'error');
                return;
            }
        }

        if (!selectedRow || !selectedStatus) {
            showSnackbar('Invalid task or status selection', 'error');
            return;
        }

        try {
            const token = sessionStorage.getItem("token");
            const payload = {
                status: selectedStatus?.value,
                actual_hours: actualHours,
                reason: reasons || ''
            };
            setCirleLoading(true);

            const response = await axios.post(
                `${BASE_URL}api/updatetaskstatus/${selectedRow.id}/`,
                payload,
                {
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            if (response.data?.status || response.status === 200) {

                const newData = tableData.map(item =>
                    item.id === selectedRow.id
                        ? { ...item, status: selectedStatus.value, actual_hours: actualHours }
                        : item
                );
                setTableData(newData);

                showSnackbar(response.data?.message || 'Task status updated successfully', 'success');

                if (onTaskUpdate) {
                    onTaskUpdate();
                }
            } else {
                showSnackbar(response.data?.message || 'Failed to update task status', 'error');
            }
        } catch (error) {
            console.error('Error updating task status:', error);
            const errorMessage = error?.response?.data?.message || error?.message || 'An error occurred while updating task status';
            showSnackbar(errorMessage, 'error');
        } finally {
            setCirleLoading(false);
            setDialogOpen(false);
            setActualHours('');
            setReasons('');
            setSelectedRow(null);
            setSelectedStatus(null);
        }
    };

    const handleDialogCancel = () => {
        setDialogOpen(false);
        setActualHours('');
        setReasons('');
        setSelectedRow(null);
        setSelectedStatus(null);
    };


    const statusOptions = [
        { value: 'Not Started', label: 'Not Started' },
        { value: 'In Progress', label: 'In Progress' },
        { value: 'Completed', label: 'Completed' },
        { value: 'Hold', label: 'Hold' },
        { value: 'Abort', label: 'Abort' }
    ];

    const getFilteredStatusOptions = (currentStatus) => {
        const options = [];
        if (currentStatus === 'Not Started') {
            options.push({ value: 'In Progress', label: 'In Progress' });
        } else if (currentStatus === 'In Progress') {
            options.push(
                { value: 'Completed', label: 'Completed' },
                { value: 'Hold', label: 'Hold' },
                { value: 'Abort', label: 'Abort' }
            );
        } else if (currentStatus === 'Hold' || currentStatus === 'Abort') {
            options.push({ value: 'In Progress', label: 'In Progress' });
        }
        // Always include current status to avoid display issues if needed, 
        // strictly following "display only" for the dropdown menu logic usually means valid *next* options.
        // But for the select value to show correctly, we just return the next options list.
        // MaterialSelect options prop usually defines the menu items.
        return options;
    };

    return (
        <div className="collapsible-table-wrapper">
            <div className="collapsible-table-container">
                <table className="collapsible-table">
                    <thead>
                        <tr>
                            <th className="expand-column"></th>
                            {displayColumns.map((col, index) => (
                                <th
                                    key={index}
                                    onClick={() => onSort && onSort(col.key)}
                                    style={{ cursor: onSort ? 'pointer' : 'default' }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <span>{col.header}</span>
                                        {sortConfig && sortConfig.key === col.key && (
                                            <span style={{ fontSize: '10px' }}>
                                                {sortConfig.direction === 'asc' ? '▲' : '▼'}
                                            </span>
                                        )}
                                    </Stack>
                                </th>
                            ))}
                            <th className="action-cell">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentData.map((row) => (
                            <React.Fragment key={row.id}>
                                {/* Main Row */}
                                <tr className="main-row">
                                    <td className="expand-cell">
                                        {collapsedColumns.length > 0 && (
                                            <button
                                                className="expand-button"
                                                onClick={() => toggleRow(row.id)}
                                                aria-label={expandedRows.includes(row.id) ? 'Collapse' : 'Expand'}
                                            >
                                                {expandedRows.includes(row.id) ? (
                                                    <FaChevronUp />
                                                ) : (
                                                    <FaChevronDown />
                                                )}
                                            </button>
                                        )}
                                    </td>
                                    {displayColumns.map((col, index) => {
                                        let cellValue = row[col.key] || '-';

                                        if (col.key === 'actual_hours') {
                                            cellValue = row.hold_date
                                                ? (Number(row.reason_hours || 0) + Number(row.actual_hours || 0)).toString()
                                                : row.actual_hours || '-';
                                        }

                                        const displayValue = truncateText(cellValue, 15);
                                        const needsTooltip = String(cellValue).length > 15;

                                        return (
                                            <td key={index}>
                                                {needsTooltip ? (
                                                    <Tooltip title={cellValue} arrow placement="top">
                                                        <span style={{ cursor: 'pointer' }}>{displayValue}</span>
                                                    </Tooltip>
                                                ) : (
                                                    displayValue
                                                )}
                                            </td>
                                        );
                                    })}
                                    <td className="action-cell">
                                        {['Completed'].includes(row.status) ? (
                                            <MaterialTypography variant="caption" fontWeight="bold" color={row.status === 'Completed' ? 'success' : 'error'}>
                                                {row.status}
                                            </MaterialTypography>
                                        ) : (
                                            <div style={{ width: '100%' }}>
                                                <MaterialSelect
                                                    placeholder="Select Status"
                                                    size="small"
                                                    options={getFilteredStatusOptions(row.status)}
                                                    onChange={(option) => handleStatusChange(option, row)}
                                                    value={statusOptions.find(opt => opt.value === row.status)}
                                                    menuPortalTarget={document.body}
                                                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                                />
                                            </div>
                                        )}
                                    </td>
                                </tr>

                                {/* Collapsed Row */}
                                {expandedRows.includes(row.id) && collapsedColumns.length > 0 && (
                                    <tr className="collapsed-row">
                                        <td colSpan={displayColumns.length + 2}>
                                            <div className="collapsed-content">
                                                <div className="collapsed-grid">
                                                    {collapsedColumns.map((col, index) => (
                                                        <div key={index} className="collapsed-item">
                                                            <span className="collapsed-label">{col.header}:</span>
                                                            <span className="collapsed-value">{row[col.key] || '-'}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
                {currentData.length === 0 && (
                    <div className="no-data">
                        <p>No data available</p>
                    </div>
                )}
            </div>

            {/* Pagination */}
            {totalRows > 0 && (

                <div className="pagination-wrapper">
                    <div className="pagination-info">
                        <span className="rows-info">
                            Showing {startIndex + 1} to {endIndex} of {totalRows} entries
                        </span>

                        <div className="rows-per-page">
                            <label htmlFor="rowsPerPage">Rows per page:</label>
                            <select
                                id="rowsPerPage"
                                value={rowsPerPage}
                                onChange={handleRowsPerPageChange}
                                className="rows-select"
                            >
                                <option value={10}>10</option>
                                <option value={30}>30</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </div>
                    </div>

                    <div className="pagination-controls">
                        <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(1)}
                            disabled={currentPage === 1}
                            title="First Page"
                        >
                            <FaAngleDoubleLeft />
                        </button>
                        <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            title="Previous Page"
                        >
                            <FaChevronLeft />
                        </button>

                        <div className="page-numbers">
                            {getPageNumbers().map((page, index) => (
                                page === '...' ? (
                                    <span key={`ellipsis-${index}`} className="page-ellipsis">...</span>
                                ) : (
                                    <button
                                        key={page}
                                        className={`page-number ${currentPage === page ? 'active' : ''}`}
                                        onClick={() => handlePageChange(page)}
                                    >
                                        {page}
                                    </button>
                                )
                            ))}
                        </div>

                        <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            title="Next Page"
                        >
                            <FaChevronRight />
                        </button>
                        <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(totalPages)}
                            disabled={currentPage === totalPages}
                            title="Last Page"
                        >
                            <FaAngleDoubleRight />
                        </button>
                    </div>
                </div>
            )}

            <ConfirmationDialog
                open={dialogOpen}
                title="Update Status"
                message={`Please provide details for marking as ${selectedStatus?.label}`}
                showCancel
                showConfirm
                onConfirm={handleDialogConfirm}
                onCancel={handleDialogCancel}
                customSection={
                    <Stack spacing={2}>
                        <Stack spacing={0.5}>
                            <MaterialTypography variant="caption" color="text">
                                Actual Hours <span style={{ color: "red" }}>*</span>
                            </MaterialTypography>
                            <MaterialInput
                                type="number"
                                size="small"
                                placeholder="Enter Actual Hours"
                                value={actualHours}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                                        setActualHours(value);
                                    }
                                }}
                            />
                        </Stack>
                        <Stack spacing={0.5}>
                            <MaterialTypography variant="caption" color="text">
                                Comment {['Abort', 'Hold'].includes(selectedStatus?.value) && <span style={{ color: "red" }}>*</span>}
                            </MaterialTypography>
                            <MaterialInput
                                multiline
                                size="small"
                                rows={3}
                                placeholder={['Abort', 'Hold'].includes(selectedStatus?.value)
                                    ? "Comment is required for this status"
                                    : "Enter Comment (Optional)"}
                                value={reasons}
                                onChange={(e) => setReasons(e.target.value)}
                            />
                        </Stack>
                    </Stack>
                }
            />
        </div>
    );
};

export default EmployeeTaskTable;
